import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { EnvelopeSimple, PaperPlaneTilt, CheckCircle, ChatCircleText, WarningCircle, CalendarCheck } from "@phosphor-icons/react";

function Metric({ label, value, suffix, testid }) {
  return (
    <div className="bg-slate-50 border border-slate-100 rounded-xl p-4" data-testid={testid}>
      <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-400">{label}</p>
      <p className="font-heading text-3xl font-black mt-1">{value}<span className="text-base font-bold text-slate-400">{suffix}</span></p>
    </div>
  );
}

export default function DigestPage() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/digest/weekly").then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/digest/send");
      if (data.status === "queued") toast.success(data.note);
      else toast.warning(data.note);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (!d) return <p className="text-slate-400">Building your weekly digest…</p>;

  return (
    <div className="max-w-3xl space-y-8 rise-in" data-testid="digest-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-slate-400">Founder Weekly Digest</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-2 flex items-center gap-3">
            <EnvelopeSimple size={36} weight="duotone" className="text-emerald-600" /> Monday Summary
          </h1>
          <p className="text-slate-500 mt-3">Preview of the weekly ops email — readiness, task compliance and floor credit scores rolled into one send.</p>
        </div>
        <button data-testid="digest-send-button" onClick={send} disabled={busy}
          className="h-12 px-6 rounded-full bg-slate-900 text-white text-sm font-bold flex items-center gap-2 tap-press hover:bg-slate-800 disabled:opacity-50 shrink-0">
          <PaperPlaneTilt size={18} /> {busy ? "Queuing…" : "Queue Monday digest"}
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm" data-testid="digest-email-preview">
        <div className="bg-slate-900 text-white px-8 py-6">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-400">Practitality Weekly Ops Digest</p>
          <h2 className="font-heading text-2xl font-bold mt-1">{d.org_name}</h2>
          <p className="text-sm text-slate-300 mt-1">{d.week_start} → {d.week_end}</p>
        </div>
        <div className="p-8 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <Metric label="Shift readiness" value={d.readiness_rate} suffix="%" testid="digest-readiness" />
            <Metric label="Task compliance" value={d.task_compliance} suffix="%" testid="digest-compliance" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm font-semibold text-slate-600">
            <span className="flex items-center gap-1.5"><CheckCircle size={16} className="text-emerald-500" /> {d.tasks_completed}/{d.tasks_total} tasks ({d.on_time_rate}% on time)</span>
            <span className="flex items-center gap-1.5"><CalendarCheck size={16} className="text-slate-400" /> {d.attendance_days} attendance days</span>
            <span className="flex items-center gap-1.5"><ChatCircleText size={16} className="text-slate-400" /> {d.reviews_total} reviews ({d.reviews_negative} negative)</span>
            <span className="flex items-center gap-1.5"><WarningCircle size={16} className="text-amber-500" /> {d.open_escalations} open escalations</span>
          </div>
          <p className="text-xs text-slate-400 border-t border-slate-100 pt-4">
            Sent every Monday to the Founder's alert email. Email delivery activates once a provider key is configured in Developer Admin → Master Keys.
          </p>
        </div>
      </div>
    </div>
  );
}
